import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { LockKey, ArrowLeft } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between bg-[var(--navy)] text-white p-12 relative overflow-hidden grain">
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-[var(--gold)]/15 blur-3xl" />
        <a href="/" className="flex items-center gap-2.5 relative" data-testid="login-home-link">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-[var(--gold)] text-[var(--navy)] font-display font-bold">
            Q
          </span>
          <span className="font-display text-xl">Quick Maid Services</span>
        </a>
        <div className="relative">
          <h1 className="font-display text-5xl tracking-tight leading-tight">
            Admin <span className="italic text-[var(--gold)]">Console</span>
          </h1>
          <p className="mt-4 text-white/70 max-w-sm">
            Manage booking requests, update statuses and keep your customers
            cared for.
          </p>
        </div>
        <p className="relative text-white/40 text-sm">We Care Like Family</p>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <a href="/" className="lg:hidden inline-flex items-center gap-1.5 text-sm text-slate-500 mb-8">
            <ArrowLeft size={16} /> Back to site
          </a>
          <div className="grid place-items-center h-14 w-14 rounded-2xl bg-[var(--navy)]/8 text-[var(--navy)] mb-6">
            <LockKey weight="duotone" size={30} />
          </div>
          <h2 className="font-display text-3xl text-[var(--navy)] tracking-tight">
            Welcome back
          </h2>
          <p className="text-slate-500 mt-1 mb-8">Sign in to your dashboard.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@quickmaidservices.in"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>
            {error && (
              <p className="text-sm text-red-600 font-medium" data-testid="login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[var(--navy)] hover:bg-[var(--navy-deep)] font-bold py-6"
              data-testid="login-submit"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
